import { useMemo } from 'react';
import { Box, HStack, VStack, useTheme } from 'native-base';
import FeatherIcon from 'react-native-vector-icons/Feather';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { isEmpty, map, range, sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';

import { Button, Card, CircularProgress, Text } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { StringTranslation } from '@pimm/services/lib/sms-workforce';
import { useGoalTypes } from '../context/goal-types.context';

type CustomerSatisfactorySurveyProps = {
  _container?: React.ComponentProps<typeof VStack>;
};

export const CustomerSatisfactorySurvey = ({ _container }: CustomerSatisfactorySurveyProps) => {
  const { colors } = useTheme();
  const { translate } = useAppLocale();
  const { t } = useTranslation();
  const goalTypes = useGoalTypes();
  const survey = useMemo(() => {
    let goals: { title: string; translations?: StringTranslation[]; value?: number }[] = map(sortBy(goalTypes.data, 'seq'), goal => ({
      title: goal.title as string,
      translations: goal.translations,
      value: goal.score,
    }));

    if (isEmpty(goals)) {
      goals = [
        { title: 'osat', value: undefined },
        { title: 'taste', value: undefined },
        { title: 'accuracy', value: undefined },
        { title: 'speed', value: undefined },
        { title: 'friendliness', value: undefined },
        { title: 'cleanliness', value: undefined },
      ];
    }
    return {
      customerExperience: {
        attachment: false,
        title: 'store_management:customer_experience',
        scores: [
          { title: 'store_management:zone_of_defection', value: 33.3 },
          { title: 'store_management:likely_to_recommend' },
          { title: 'store_management:likely_to_return', value: 52.9 },
          { title: 'store_management:experience_a_problem', value: 27.8 },
          { title: 'store_management:problem_resolution', value: 0 },
        ],
      },
      keyDriverAndPlays: {
        attachment: true,
        title: 'store_management:key_drivers_and_plays',
        desc: 'store_management:last_ninety_days',
        scores: goals,
      },
    };
  }, [goalTypes.data]);

  const handlePressOpenPortal = () => {
    window.open('https://reporting.smg.com/index.aspx', '_blank', 'noreferrer');
  };

  return (
    <VStack space={3} py={1} {..._container}>
      <Text size="2xl" fontWeight={700} color="gray.900">
        {t('store_management:customer_satisfaction_surveys')}
      </Text>

      {[survey.keyDriverAndPlays].map(survey => {
        return (
          <Card key={survey.title} rounded="xl" pt={1.5}>
            <HStack mb={5} h={12} borderBottomWidth={1} alignItems="center" justifyContent="space-between">
              <Text size="lg" fontWeight={700} color="black">
                {`${t(survey.title)} ${survey.desc ? `(${t(survey.desc)})` : ''}`}
              </Text>
              {/*  TODO: Will Enable after convention
               {!!survey.attachment && <IconButton rounded="full" p={2} icon={<FileAttachmentIcon size="md" color="primary.500" />} />} */}
            </HStack>
            <HStack alignItems="center">
              <HStack space={3} rounded="3xl" alignItems="center" p={4} backgroundColor="gray.100">
                <Box style={{ maxWidth: 80 }}>
                  <Text size="lg" fontWeight={700} color="black" textAlign="center">
                    {t(`store_management:${survey.scores[0].title.toLocaleLowerCase()}`)}
                  </Text>
                </Box>
                <CircularProgress
                  _text={{ size: 'xl', decimalScale: 1, suffix: '%' }}
                  activeStrokeColor={colors.blueLight[500]}
                  inActiveStrokeColor={colors.white}
                  radius={55}
                  strokeWidth={16}
                  value={survey.scores[0].value ?? 0}
                />
              </HStack>
              {range(1, survey.scores.length).map(index => {
                const score = survey.scores[index];
                return (
                  <VStack key={score.title} flex={1} space={1} alignItems="center" justifyContent="center">
                    <Box alignItems="center" justifyContent="center" h={9} width={76}>
                      <Text size="md" fontWeight={500} color="black" textAlign="center" lineHeight="sm">
                        {t(`store_management:${score.title.toLocaleLowerCase()}`)}
                      </Text>
                    </Box>
                    <Box position="relative" alignItems="center" justifyContent="center">
                      <CircularProgress
                        _text={{ size: 'lg', decimalScale: 1, suffix: '%' }}
                        activeStrokeColor={colors.blueLight[500]}
                        inActiveStrokeColor={colors.gray[200]}
                        radius={45}
                        strokeWidth={13}
                        value={score.value ?? 0}
                      />

                      {score.value === undefined && (
                        <Box position="absolute" alignItems="center" justifyContent="center" w={16} h={16} bgColor="white" rounded="full">
                          <Text size="lg" fontWeight={500} color="black" textAlign="center">
                            {t('store_management:na')}
                          </Text>
                        </Box>
                      )}
                    </Box>
                  </VStack>
                );
              })}
            </HStack>
          </Card>
        );
      })}

      <HStack space={3} justifyContent="flex-end">
        <Button variant="unstyled" w={130} endIcon={<FeatherIcon name="arrow-up-right" size={18} />} onPress={handlePressOpenPortal}>
          {`${t('store_management:smg')} 360`}
        </Button>
        <Button
          variant="unstyled"
          w={130}
          disabled
          endIcon={<Ionicons name="ios-cloud-download-outline" size={18} style={{ paddingLeft: 5 }} />}
        >
          {t('store_management:download')}
        </Button>
      </HStack>
    </VStack>
  );
};
